import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { memo } from "react";
import {
  BookingPostSubmissionActivityType,
  ContentBriefDeliverableActivityType,
  MediaType,
} from "../../graphql/generated";
import styled from "../../styles";
import Avatar, { InitialsAvatar } from "../Avatar";
import { Flex } from "../Flex";
import VideoUploaded from "../icons/VideoUploaded";
import { Text } from "../Text";
import { View } from "../View";
import { ActivityItem } from "./types";

interface ActivityFeedProps {
  activities: ActivityItem[];
  mediaType: MediaType;
  className?: string;
  seekToTime: (time: number) => void;
}

export const ActivityFeed = memo(
  ({ activities, mediaType, className, seekToTime }: ActivityFeedProps) => {
    if (!activities || activities.length === 0) {
      return (
        <EmptyState className={className}>
          <Text size="s" colorPreset="secondary">
            No activity yet
          </Text>
        </EmptyState>
      );
    }

    return (
      <FeedContainer className={className}>
        {activities.map((item, index) => (
          <ActivityItemComponent
            key={item.id}
            activity={item}
            mediaType={mediaType}
            seekToTime={seekToTime}
            isLastItem={index === activities.length - 1}
          />
        ))}
      </FeedContainer>
    );
  }
);

ActivityFeed.displayName = "ActivityFeed";

interface ActivityItemProps {
  activity: ActivityItem;
  mediaType: MediaType;
  seekToTime: (time: number) => void;
  isLastItem: boolean;
}

const ActivityItemComponent = memo(
  ({ activity, mediaType, seekToTime, isLastItem }: ActivityItemProps) => {
    const avatarUrl =
      activity.performedByType === "CREATOR"
        ? activity.performedBy?.creators?.[0]?.preferredProfile?.avatar
        : undefined;

    const firstName = activity.performedBy?.firstName || "Unknown";
    const isVideo = mediaType === MediaType.Video;
    const formattedTime = activity.commentTimestampSeconds
      ? formatTimestamp(activity.commentTimestampSeconds)
      : null;

    // Check if this is a comment activity
    const isComment =
      activity.type === BookingPostSubmissionActivityType.Comment ||
      activity.type === ContentBriefDeliverableActivityType.Comment ||
      (activity.comment && activity.comment.trim() !== "");

    if (isComment) {
      return (
        <ActivityWrapper>
          <CommentWrapper>
            <Flex align="flex-start" gap="s">
              <View style={{ flex: 1 }}>
                <Flex align="center" gap="xs" margin="0 0 xs 0">
                  {avatarUrl ? (
                    <Avatar
                      size={22}
                      url={avatarUrl}
                      borderSize={0}
                      withShadow={false}
                    />
                  ) : (
                    <InitialsAvatar
                      name={`${activity.performedBy?.firstName || ""} ${
                        activity.performedBy?.lastName || ""
                      }`}
                      size={22}
                    />
                  )}
                  <Text size="xs" weight="bold" margin="xxs 0 0 xxs">
                    {firstName}
                  </Text>
                  <Text size="xxs" colorPreset="secondary" margin="xxs 0 0">
                    {" "}
                    ·{" "}
                    {formatDistanceToNowStrict(
                      fromUnixTime(activity.createdAt),
                      { addSuffix: true }
                    )}
                  </Text>
                </Flex>
                <Text size="s" margin="s 0 0">
                  {formattedTime && (
                    <>
                      <Timestamp
                        onClick={() =>
                          seekToTime(activity.commentTimestampSeconds!)
                        }
                      >
                        {formattedTime}
                      </Timestamp>{" "}
                    </>
                  )}
                  {activity.comment}
                </Text>
              </View>
            </Flex>
          </CommentWrapper>
          {!isLastItem && <ConnectingLine />}
        </ActivityWrapper>
      );
    }

    // Activity type for uploads/revisions
    return (
      <ActivityWrapper>
        <ActivityRow>
          <IconContainer>
            <VideoUploaded width={24} height={24} colorPreset="text" />
          </IconContainer>
          <View style={{ marginTop: 5, flex: 1 }}>
            <Text size="xs" weight="semi" margin="0">
              {firstName} uploaded{" "}
              {activity.type === "REVISION"
                ? "a new version"
                : isVideo
                ? "a video"
                : "an image"}
              <Text
                size="xxs"
                colorPreset="secondary"
                isInline
                margin="0 0 0 xs"
              >
                {" · "}
                {formatDistanceToNowStrict(fromUnixTime(activity.createdAt), {
                  addSuffix: true,
                })}
              </Text>
            </Text>
          </View>
        </ActivityRow>
        {!isLastItem && <ConnectingLine />}
      </ActivityWrapper>
    );
  }
);

ActivityItemComponent.displayName = "ActivityItemComponent";

function formatTimestamp(time: number) {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${seconds}`;
}

const FeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${(p) => p.theme.spacing.l};
`;

const ActivityWrapper = styled.div`
  position: relative;
`;

const ActivityRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const IconContainer = styled.div`
  margin-right: ${(p) => p.theme.spacing.xs};
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  color: ${(p) => p.theme.color.typography.text};
`;

const CommentWrapper = styled.div`
  border: 1px solid ${(p) => p.theme.color.card.divider};
  border-radius: ${(p) => p.theme.misc.borderRadius};
  padding: ${(p) => p.theme.spacing.s};
  background: ${(p) => p.theme.color.card.background};
`;

const ConnectingLine = styled.div`
  width: 0.5px;
  height: 8px;
  background-color: ${(p) => p.theme.color.typography.secondary}33;
  margin-left: 14px;
  margin-top: ${(p) => p.theme.spacing.xs};
  margin-bottom: ${(p) => p.theme.spacing.xs};
`;

const EmptyState = styled.div`
  padding: ${(p) => p.theme.spacing.l} 0;
  display: flex;
  justify-content: center;
`;

const Timestamp = styled.span`
  font-size: 13px;
  color: ${(p) => p.theme.color.constructive};
  font-weight: ${(p) => p.theme.typography.weight.bold};
  background-color: ${(p) => p.theme.color.constructive}30;
  padding: 1px 8px 0px;
  border-radius: 5px;
  cursor: pointer;
`;
